import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';

/**
 * 把任意异常翻译成给用户看的一句话。
 *
 * 后端只返错误码，文案在这里按当前语言拼出来 —— 双语要覆盖到错误提示，
 * 不然中文界面弹出一句英文 "Out of geofence" 就很糟。
 */
export function useErrorMessage() {
  const { t } = useTranslation();

  return useCallback(
    (error: unknown): string => {
      if (error instanceof ApiError) {
        if (error.code === 'network') return t('errors.network');

        const key = `errors.${error.code}`;
        const translated = t(key, { ...error.params, defaultValue: '' });
        if (translated) return translated + devDetail(error);

        // 漏翻的错误码：显示通用文案，同时把 code 打到控制台方便补齐语言文件
        console.warn(`[i18n] 缺少错误码翻译：${error.code}`);
        return t('errors.unknown');
      }
      return t('errors.unknown');
    },
    [t],
  );
}

/**
 * 开发环境下把服务端的校验详情接在提示后面。
 *
 * 后端返的 `params.fields` 是 class-validator 的原始英文
 * （`requirements.fields.0.labelEn must be longer than or equal to 1 characters`）。
 * **这东西绝不能给用户看** —— 工头看不懂它，真正能帮他的是表单上标红的那一行。
 * 但排查时它很有用，所以保留在响应体里，只在开发构建里显示出来，
 * 生产构建会被 Vite 整段去掉。
 */
function devDetail(error: ApiError): string {
  if (!import.meta.env.DEV) return '';
  const fields = error.params?.fields;
  return fields ? `（dev: ${String(fields)}）` : '';
}
